import api from '@/services/api'

export type PjNfMensalStatus =
  | 'AGUARDANDO_NF'
  | 'NF_ENVIADA'
  | 'EM_VALIDACAO'
  | 'APROVADA'
  | 'REJEITADA'
  | 'PAGA'

export type PjNfParsePreview = {
  valid: boolean
  canSubmitWithRessalvas: boolean
  errors: string[]
  ressalvas: string[]
  warnings: string[]
  parsed: {
    numeroNf: string | null
    dataEmissao: string | null
    cnpjEmissor: string | null
    source: 'xml' | 'pdf' | 'unknown'
  }
}

export type PjNfMensalRow = {
  id: string
  pjAgradaId: string
  competencia: string
  status: PjNfMensalStatus
  valorEsperado: number | null
  numeroNf: string | null
  dataEmissao: string | null
  nfFileUrl: string | null
  nfFileName: string | null
  nfMimeType: string | null
  enviadoEm: string | null
  validadoPorId: string | null
  validadoEm: string | null
  rejeicaoMotivo: string | null
  pagoEm: string | null
  pagoPorId: string | null
  pagamentoId: string | null
  enviadoComRessalvas: boolean
  alertasEnvio: string | null
  createdAt: string
  updatedAt: string
  prestador?: {
    id: string
    nomeCompleto: string
    email: string | null
    cnpj: string | null
    setor: string | null
    areaAtuacao: string | null
    remuneracaoMensal: number | null
    portalUserId: string | null
  }
  validadoPor?: { id: string; name: string } | null
  pagoPor?: { id: string; name: string } | null
}

export type PjNfCompetenciaSummary = {
  competencia: string
  total: number
  aguardando: number
  enviadas: number
  emValidacao: number
  aprovadas: number
  rejeitadas: number
  pagas: number
}

export type PjNfPortalLinkStatus =
  | { linked: false }
  | {
      linked: true
      pjId: string
      nomeCompleto: string
      status: string
      dataInicioContrato: string | null
    }

export type PjNfPortalMineResponse = {
  linked: {
    pjId: string
    nomeCompleto: string
    dataInicioContrato: string | null
  }
  data: PjNfMensalRow[]
}

export const pjNfMensalService = {
  async listCompetencia(
    competencia: string,
    params?: { status?: PjNfMensalStatus; search?: string },
  ) {
    const { data } = await api.get<{ data: PjNfMensalRow[] }>(`/pj-nf/competencias/${competencia}`, {
      params,
    })
    return data.data
  },

  async getCompetenciaSummary(competencia: string) {
    const { data } = await api.get<{ data: PjNfCompetenciaSummary }>(
      `/pj-nf/competencias/${competencia}/summary`,
    )
    return data.data
  },

  async getById(id: string) {
    const { data } = await api.get<{ data: PjNfMensalRow }>(`/pj-nf/${id}`)
    return data.data
  },

  async listMine(): Promise<PjNfPortalMineResponse> {
    const { data } = await api.get<PjNfPortalMineResponse>('/pj-nf/me')
    return data
  },

  async getPortalLinkStatus() {
    const { data } = await api.get<{ data: PjNfPortalLinkStatus }>('/pj-nf/me/link-status')
    return data.data
  },

  async parseNf(file: File, competencia: string): Promise<PjNfParsePreview> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post<{ data: PjNfParsePreview }>('/pj-nf/parse', form, {
      params: { competencia },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data.data
  },

  async uploadAttachment(file: File) {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post<{
      data: {
        fileUrl: string
        fileName: string
        mimeType: string
        parsed: PjNfParsePreview['parsed']
      }
    }>('/pj-nf/attachments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data.data
  },

  async submit(
    id: string,
    body: {
      nfFileUrl: string
      nfFileName?: string
      nfMimeType?: string
      confirmarRessalvas?: boolean
    },
  ) {
    const { data } = await api.post<{ data: PjNfMensalRow }>(`/pj-nf/${id}/submit`, body)
    return data.data
  },

  async resubmit(
    id: string,
    body: {
      nfFileUrl: string
      nfFileName?: string
      nfMimeType?: string
      confirmarRessalvas?: boolean
    },
  ) {
    const { data } = await api.post<{ data: PjNfMensalRow }>(`/pj-nf/${id}/resubmit`, body)
    return data.data
  },

  async marcarEmValidacao(id: string) {
    const { data } = await api.patch<{ data: PjNfMensalRow }>(`/pj-nf/${id}/em-validacao`)
    return data.data
  },

  async validar(id: string, body: { aprovada: boolean; rejeicaoMotivo?: string }) {
    const { data } = await api.patch<{ data: PjNfMensalRow }>(`/pj-nf/${id}/validar`, body)
    return data.data
  },

  async pagar(id: string, body?: { observacoes?: string }) {
    const { data } = await api.patch<{ data: PjNfMensalRow }>(`/pj-nf/${id}/pagar`, body ?? {})
    return data.data
  },
}
